import { afterNextRender, ChangeDetectionStrategy, Component, computed, effect, inject, Injector, input, untracked } from '@angular/core'
import { Dialog } from 'primeng/dialog'
import { ButtonDirective } from 'primeng/button'
import { AlbertModalService } from '@services/albert-modal.service'
import { AlbertModalAction, AlbertModalSlot, AlbertModalType } from './albert-modal.types'
import { SharedModule } from 'primeng/api'
import { NgTemplateOutlet } from '@angular/common'
import { blockBodyScroll } from 'primeng/dom'

@Component({
  selector: 'albert-modal',
  standalone: true,
  imports: [Dialog, ButtonDirective, SharedModule, NgTemplateOutlet],
  templateUrl: './albert-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AlbertModalComponent {
  /** Which modal of the service this instance renders; the app renders the default slot above the content one. */
  public readonly slot = input<AlbertModalSlot>('default')
  public readonly isVisible = computed(() => this.config() !== null)
  private readonly actions = computed<AlbertModalAction[]>(() => {
    const config = this.config()
    if (!config) return []
    const actions = config.actions ?? []
    if (actions.length === 0) {
      return [{ label: 'Close', position: 'right', severity: 'secondary', callback: () => this.onClose() }]
    }
    return actions
  })
  public readonly leftActions = computed(() => this.actions().filter(a => a.position === 'left'))
  public readonly rightActions = computed(() => this.actions().filter(a => a.position === 'right'))
  private readonly modalService = inject(AlbertModalService)
  public readonly config = computed(() => {
    switch (this.slot()) {
      case 'content':
        return this.modalService.contentConfig()
      case 'prompt':
        return this.modalService.promptConfig()
      case 'default':
        return this.modalService.config()
    }
  })
  private readonly injector = inject(Injector)

  constructor() {
    // PrimeNG unlocks the page when a destroyed dialog leaves at most one other open, so a modal below a closed
    // error or prompt would no longer block scrolling. Untracked, so only closing this slot runs the check.
    effect(() => {
      if (this.config() === null && untracked(() => this.hasModalBelow())) {
        afterNextRender(() => {
          if (this.hasModalBelow()) {
            blockBodyScroll()
          }
        }, { injector: this.injector })
      }
    })
  }

  /** Handles the X: the config's onClose hook decides, otherwise the modal closes. */
  public requestClose(): void {
    const onClose = this.config()?.onClose
    if (onClose) {
      onClose()
    } else {
      this.onClose()
    }
  }

  public onClose(): void {
    this.modalService.hideSlot(this.slot())
  }

  private hasModalBelow(): boolean {
    switch (this.slot()) {
      case 'default':
        return this.modalService.promptConfig() !== null || this.modalService.contentConfig() !== null
      case 'prompt':
        return this.modalService.contentConfig() !== null
      case 'content':
        return false
    }
  }

  public handleAction(action: AlbertModalAction): void {
    action.callback()
  }

  public getIconClass(type: AlbertModalType): string {
    switch (type) {
      case 'info':
        return 'pi pi-info-circle'
      case 'warning':
        return 'pi pi-exclamation-triangle'
      case 'error':
        return 'pi pi-exclamation-circle'
      default:
        return 'pi pi-info-circle'
    }
  }

  public getButtonSeverity(type: AlbertModalType): 'success' | 'info' | 'warn' | 'danger' | 'help' | 'primary' | 'secondary' | 'contrast' | null | undefined {
    switch (type) {
      case 'info':
        return 'info'
      case 'warning':
        return 'warn'
      case 'error':
        return 'danger'
      default:
        return 'primary'
    }
  }
}
